import { INITIAL_TABLE_HEADERS, CALCULATED_KEYS } from "@/config/reportStructure";
import { getResultStateByPeriod } from "@/api/financialReport";
import { consoleHandlerError, extractErrorMessage } from "@/utils/message";
import { resolveDateRange, buildHeadersByPeriod, formatPeriodLabel } from "./utils/dateRange";

// Deep clone helper centralizado para INITIAL_TABLE_HEADERS — Vue 2 escribe
// sobre las entradas (`itemsDict`, `items`, `isShowed`) en cada fetch, así
// que cada inicialización debe partir de una copia independiente. El dict
// es plano (strings/numbers/booleans + arrays nested), JSON-clonable.
// Centralizar acá facilita migrar a `structuredClone` cuando Node 17+ /
// jsdom acompañe.
function cloneInitialHeaders() {
    return JSON.parse(JSON.stringify(INITIAL_TABLE_HEADERS));
}

const state = () => ({
    filterPeriod: "month",
    tableRowHeaders: cloneInitialHeaders(),
    netSellsDetail: {},
    maxCostsDetail: {},
    maxExpensesDetail: {},
    isLoading: false,
    error: null
});

const getters = {
    // Las cabeceras se derivan del MISMO rango con el que `fetchReportData`
    // le pega al backend, vía `resolveDateRange(_, _, filterPeriod)` +
    // `buildHeadersByPeriod`. Antes hacíamos un switch + loop manual con
    // números hardcoded (12 / 24), lo que producía 13 / 25 cabeceras
    // (off-by-one inclusivo) y se desincronizaba cuando el cálculo del
    // rango cambiaba — exactamente el bug que motivó esta refactorización.
    getHeadersTable: (state, _getters, rootState) => () => {
        const { initDate, endDate } = resolveDateRange(
            rootState.reportsFilters.endMonth,
            rootState.reportsFilters.selectedPeriodRange,
            state.filterPeriod,
            rootState.reportsFilters.customRange,
            rootState.reportsFilters.customYearRange,
        );
        return buildHeadersByPeriod(initDate, endDate, state.filterPeriod);
    }
};

const mutations = {
    SET_FILTER_PERIOD(state, period) {
        state.filterPeriod = period;
    },
    SET_REPORT_DATA(state, data) {
        state.tableRowHeaders = data.tableRowHeaders;
        state.netSellsDetail = data.netSellsDetail;
        state.maxCostsDetail = data.maxCostsDetail;
        state.maxExpensesDetail = data.maxExpensesDetail;
    },
    TOGGLE_CHILD_ROWS(state, isExpanded) {
        const childKeys = ["gastos_administracion", "gastos_comerciales", "gastos_i+d"];
        childKeys.forEach(key => {
            if (state.tableRowHeaders[key]) {
                state.tableRowHeaders[key].isShowed = isExpanded;
            }
        });
    },
    SET_LOADING(state, isLoading) {
        state.isLoading = isLoading;
    },
    SET_ERROR(state, error) {
        state.error = error;
    }
};

const actions = {
    async fetchReportData({ state, commit, getters, rootState }) {
        commit("SET_LOADING", true);
        try {
            // Mismo cálculo que el getter — pasamos filterPeriod a
            // resolveDateRange para que la regla year/ytd quede aplicada en un
            // solo lugar y nunca se desincronicen cabeceras vs payload.
            const { initDate, endDate } = resolveDateRange(
                rootState.reportsFilters.endMonth,
                rootState.reportsFilters.selectedPeriodRange,
                state.filterPeriod,
                rootState.reportsFilters.customRange,
                rootState.reportsFilters.customYearRange,
            );

            const response = await getResultStateByPeriod(state.filterPeriod, initDate, endDate);

            const data = response.data.report_data;
            const allKeys = response.data.all_keys || [];

            const tableRowHeaders = cloneInitialHeaders();

            // Process visibility and raw data
            Object.keys(tableRowHeaders).forEach(key => {
                // Set visibility based on backend's all_keys
                tableRowHeaders[key].isShowed = allKeys.includes(key);

                if (data && data[key]) {
                    tableRowHeaders[key].itemsDict = {};
                    tableRowHeaders[key].prevYearDict = {};
                    tableRowHeaders[key].prevPeriodDict = {};
                    data[key].forEach(item => {
                        // La key DEBE salir de `formatPeriodLabel` — la misma
                        // fuente que arma las cabeceras (`buildHeadersByPeriod`)
                        // contra las que la tabla hace el lookup `itemsDict[header]`.
                        // Formatear acá a mano dejaba la key en minúscula ("ene
                        // 2025") mientras la cabecera venía capitalizada ("Ene
                        // 2025"), y el lookup fallaba → montos en cero. Es el
                        // mismo patrón que ya usan costsReport/expensesReport/etc.
                        const keyPeriod = formatPeriodLabel(item.period, state.filterPeriod);
                        tableRowHeaders[key].itemsDict[keyPeriod] = Number(item.total_amount);
                        // Peer del año anterior (mismo bucket 12m atrás) que el
                        // backend matchea por celda — denominador del YoY del
                        // tooltip. `null` cuando no hay peer (cuenta sin
                        // movimiento hace un año); lo guardamos crudo (null el
                        // tooltip lo pinta como '—', igual que un 0).
                        tableRowHeaders[key].prevYearDict[keyPeriod] =
                            item.prev_year_amount == null ? null : Number(item.prev_year_amount);
                        // Peer del período anterior (MoM/QoQ) que matchea el
                        // backend por celda. Da peer real a la primera columna.
                        tableRowHeaders[key].prevPeriodDict[keyPeriod] =
                            item.prev_period_amount == null ? null : Number(item.prev_period_amount);
                    });
                } else {
                    tableRowHeaders[key].itemsDict = {};
                    tableRowHeaders[key].prevYearDict = {};
                    tableRowHeaders[key].prevPeriodDict = {};
                }
            });

            // Perform calculations based on the explicit CALCULATED_KEYS list
            const headers = getters.getHeadersTable();

            CALCULATED_KEYS.forEach(key => {
                const config = tableRowHeaders[key];
                if (!config) return;
                if (!config.prevYearDict) config.prevYearDict = {};
                if (!config.prevPeriodDict) config.prevPeriodDict = {};

                headers.forEach(header => {
                    const val = calculateKey(
                        tableRowHeaders,
                        config.keysSum || [],
                        config.keysSubtract || [],
                        config.keyDivide || "",
                        header
                    );

                    if (config.isMoney) {
                        tableRowHeaders[key].itemsDict[header] = val;
                        // YoY sólo se muestra en filas de dinero (las de ratio no
                        // llevan tooltip), así que su prev-year se computa igual
                        // que el valor pero sobre `prevYearDict` de las cuentas
                        // crudas — todas las filas calculadas de dinero (EBITDA,
                        // gastos_totales, no_operacional, utilidad) dependen sólo
                        // de cuentas crudas, ya pobladas en la fase anterior.
                        tableRowHeaders[key].prevYearDict[header] = calculateKey(
                            tableRowHeaders,
                            config.keysSum || [],
                            config.keysSubtract || [],
                            config.keyDivide || "",
                            header,
                            "prevYearDict"
                        );
                        // Mismo cálculo para el peer del período anterior (MoM/QoQ).
                        tableRowHeaders[key].prevPeriodDict[header] = calculateKey(
                            tableRowHeaders,
                            config.keysSum || [],
                            config.keysSubtract || [],
                            config.keyDivide || "",
                            header,
                            "prevPeriodDict"
                        );
                    } else {
                        // Filas de ratio (margen %, gastos/ventas %): porcentaje
                        // ENTERO (regla del sistema, ver CLAUDE.md #9). Guardamos
                        // un number redondeado, no un string `.toFixed` — el dato
                        // crudo debe ser consistente con las filas de dinero.
                        tableRowHeaders[key].itemsDict[header] = Math.round(val * 100);
                    }
                });
            });

            commit("SET_REPORT_DATA", {
                tableRowHeaders,
                netSellsDetail: response.data.ventas_netas_detail,
                maxCostsDetail: response.data.max_costs_detail,
                maxExpensesDetail: response.data.max_expenses_detail
            });
            commit("TOGGLE_CHILD_ROWS", false)
        } catch (error) {
            consoleHandlerError({ error, prefix: "[resultsReport]", silent: true });
            commit("SET_ERROR", extractErrorMessage(error, "Error al cargar el reporte."));
        } finally {
            commit("SET_LOADING", false);
        }
    }
};

// `dictField` permite correr la misma fórmula sobre el dict del año anterior
// (`prevYearDict`) para derivar el prev-year de las filas calculadas, sin
// duplicar la lógica de suma/resta/división. Default `itemsDict` = valor actual.
function calculateKey(tableRowHeaders, keysSum, keysSubtract, keyDivide, columnHeader, dictField = "itemsDict") {
    let sumAmount = 0;
    keysSum.forEach(key => {
        sumAmount += Number((tableRowHeaders[key] ?? {})[dictField]?.[columnHeader] ?? 0);
    });

    let subtractAmount = 0;
    keysSubtract.forEach(key => {
        subtractAmount += Number((tableRowHeaders[key] ?? {})[dictField]?.[columnHeader] ?? 0);
    });

    let divideAmount = 1;
    if (keyDivide !== "") {
        divideAmount = Number((tableRowHeaders[keyDivide] ?? {})[dictField]?.[columnHeader] ?? 0);
    }

    if (divideAmount === 0) return 0;
    return (sumAmount - subtractAmount) / (keyDivide !== "" ? divideAmount : 1);
}

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions
};
